// =============================================================================
// AppLayout - Main application layout with horizontal navigation (Figma design)
// =============================================================================

import { Outlet } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { TrialBanner } from '@/components/TrialBanner';
import { ForcePasswordModal } from '@/components/ForcePasswordModal';
import { TrialExpiredModal } from '@/components/TrialExpiredModal';
import { DiagBanner } from '@/components/DiagBanner';
import { useCurrentUser } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';

/**
 * Main application layout for authenticated users
 * Uses horizontal top navigation bar (Figma design)
 */
export function AppLayout() {
  // Fetch current user and organization data on mount
  const { isLoading } = useCurrentUser();
  const { activeOrganization } = useAuthStore();

  // Show loading state while fetching auth data (but only if we don't have org yet)
  if (isLoading && !activeOrganization) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#009FE4] border-t-transparent mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const { _diag } = useAuthStore();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Session diagnostics banner — fixed above everything during replay */}
      <DiagBanner />

      {/* Trial banner - non-dismissible, above navigation */}
      <TrialBanner />

      {/* Trial expired modal - shown once when trial expires */}
      <TrialExpiredModal />

      {/* Forced password creation for passwordless accounts (2026-09-15).
          z-[60] — renders above TrialExpiredModal when both trigger. */}
      <ForcePasswordModal />

      {/* Horizontal top navigation — pushed down when diag banner is visible */}
      <Navigation />

      {/* Page content - centered with max-w-7xl like Figma */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 ${_diag?.active ? 'mt-10' : ''}`}>
        <Outlet />
      </main>
    </div>
  );
}
